<template>
  <div class="bulk-attachments [&_.el-upload]:w-full [&_.el-upload-dragger]:w-full [&_.el-upload-dragger]:max-w-full max-sm:[&_.el-upload-dragger]:h-auto max-sm:[&_.el-upload-dragger]:min-h-[124px] max-sm:[&_.el-upload-dragger]:px-2 max-sm:[&_.el-upload-dragger]:py-3.5 max-sm:[&_.el-upload__text]:leading-snug">
    <div v-loading="loading">
      
      <!-- Custom Tabs -->
      <div class="mb-4 flex overflow-x-auto border-b border-gray-200">
        <button 
          class="flex-1 shrink-0 whitespace-nowrap px-3 py-2 text-sm font-medium focus:outline-none sm:flex-none sm:px-4"
          :class="activeTab === 'add' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'"
          @click="activeTab = 'add'"
        >
          Add Attachment
        </button>
        <button 
          class="flex-1 shrink-0 whitespace-nowrap px-3 py-2 text-sm font-medium focus:outline-none sm:flex-none sm:px-4"
          :class="activeTab === 'hide' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'"
          @click="activeTab = 'hide'"
        >
          Hide Attachments
        </button>
      </div>

      <!-- ADD TAB CONTENT -->
      <div v-if="activeTab === 'add'">
        <!-- Client Selection -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Client</label>
          <select 
            :value="addForm.client ? addForm.client.id : ''"
            @change="onClientSelect('add', $event.target.value)"
            class="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          >
            <option value="" disabled>Select Client</option>
            <option
              v-for="client in availableClients"
              :key="client.id"
              :value="client.id">
              {{ client.name }}
            </option>
          </select>
        </div>

        <!-- Account Selection -->
        <div class="mb-4" v-if="addForm.client">
          <label class="block text-sm font-medium text-gray-700 mb-1">Account</label>
          <select 
            :value="addForm.accountId"
            @change="onAccountSelect('add', $event.target.value)"
            class="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          >
            <option value="ALL">All Accounts ({{ getAvailableAccounts(addForm.client).length }})</option>
            <option
              v-for="account in getAvailableAccounts(addForm.client)"
              :key="account.id"
              :value="account.id">
              {{ account.name }}
            </option>
          </select>
        </div>

        <!-- Tag Selection -->
        <div class="mb-4">
          <label class="el-form-item__label">Tags (Next Month)</label>
          <el-select 
            v-model="addForm.tags" 
            multiple 
            filterable 
            placeholder="Select Tags"
            class="w-full"
            @change="handleTagChange">
            <el-option
              v-for="tag in addForm.availableTags"
              :key="tag"
              :value="tag"
              :label="tag">
            </el-option>
          </el-select>
          <div v-if="addForm.tags.length > 1" class="mt-2 text-xs text-yellow-600">
            <i class="el-icon-warning"></i> 
            Attachment will be added to bookings containing <strong>ALL</strong> selected tags.
          </div>
          <div class="mt-1 text-xs text-gray-400">
            Matching Bookings: <strong>{{ matchingAddEvents.length }}</strong>
            <el-popover
              v-if="matchingAddEvents.length > 0"
              placement="right"
              title="Matching Bookings"
              width="300"
              trigger="click">
              <div class="max-h-60 overflow-y-auto">
                <div v-for="(event, index) in matchingAddEvents" :key="event.id" class="mb-1">
                   <a :href="'#/schedule-agenda/' + event.id" target="_blank" class="text-blue-600 hover:underline text-xs">
                     {{ index + 1 }}. {{ event.name || 'Unnamed Event' }}
                   </a>
                </div>
              </div>
              <i slot="reference" class="el-icon-view ml-2 cursor-pointer text-blue-500" title="View List"></i>
            </el-popover>
          </div>
        </div>

        <!-- File Upload -->
        <div class="mb-4 flex flex-col">
          <div>
             <label class="el-form-item__label">Attachment</label>
          </div>
          <div>
            <el-upload
              class="upload-demo w-full"
              drag
              action=""
              :auto-upload="false"
              :on-change="handleFileChange"
              :on-remove="handleFileRemove"
              :file-list="fileList"
              :limit="1"
              :multiple="false"
              accept=".pdf,.doc,.docx,.txt,.csv,.jpg,.png">
              <i class="el-icon-upload"></i>
              <div class="el-upload__text">Drop file here or <em>click to upload</em></div>
            </el-upload>
          </div>
        </div>

        <!-- Success Message & Link -->
        <div v-if="uploadedFileUrl" class="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
          <div class="flex items-center mb-2">
            <i class="el-icon-success text-green-500 mr-2 text-xl"></i>
            <span class="text-green-700 font-bold">Upload Successful!</span>
          </div>
          <div class="text-sm text-gray-600 mb-2">
            File available at:
          </div>
          <a :href="uploadedFileUrl" target="_blank" class="text-blue-600 hover:text-blue-800 underline break-all block">
            {{ uploadedFileName }} <i class="el-icon-link"></i>
          </a>
        </div>

        <!-- Actions -->
        <div class="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-0 max-sm:[&_.el-button]:ml-0 max-sm:[&_.el-button]:w-full">
          <el-button @click="handleClose">{{ uploadedFileUrl ? 'Close' : 'Cancel' }}</el-button>
          <el-button 
            v-if="!uploadedFileUrl"
            type="primary" 
            @click="confirmUpload" 
            :disabled="!isAddValid || uploading" 
            :loading="uploading">
            Add Attachment
          </el-button>
        </div>
      </div>

      <!-- HIDE TAB CONTENT -->
      <div v-if="activeTab === 'hide'">
        <!-- Client Selection -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Client</label>
          <select 
            :value="hideForm.client ? hideForm.client.id : ''"
            @change="onClientSelect('hide', $event.target.value)"
            class="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          >
            <option value="" disabled>Select Client</option>
            <option
              v-for="client in availableClients"
              :key="client.id"
              :value="client.id">
              {{ client.name }}
            </option>
          </select>
        </div>

        <!-- Account Selection -->
        <div class="mb-4" v-if="hideForm.client">
          <label class="block text-sm font-medium text-gray-700 mb-1">Account</label>
          <select 
            :value="hideForm.accountId"
            @change="onAccountSelect('hide', $event.target.value)"
            class="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          >
            <option value="ALL">All Accounts ({{ getAvailableAccounts(hideForm.client).length }})</option>
            <option
              v-for="account in getAvailableAccounts(hideForm.client)"
              :key="account.id"
              :value="account.id">
              {{ account.name }}
            </option>
          </select>
        </div>

        <!-- Tag Selection -->
        <div class="mb-4">
          <label class="el-form-item__label">Tags (Next Month)</label>
          <el-select 
            v-model="hideForm.tags" 
            multiple 
            filterable 
            placeholder="Select Tags"
            class="w-full"
            @change="handleTagChange">
            <el-option
              v-for="tag in hideForm.availableTags"
              :key="tag"
              :value="tag"
              :label="tag">
            </el-option>
          </el-select>
          
          <div class="mt-1 text-xs text-gray-400">
            Matching Bookings: <strong>{{ matchingHideEvents.length }}</strong>
            <el-popover
              v-if="matchingHideEvents.length > 0"
              placement="right"
              title="Matching Bookings"
              width="300"
              trigger="click">
              <div class="max-h-60 overflow-y-auto">
                <div v-for="(event, index) in matchingHideEvents" :key="event.id" class="mb-1">
                   <a :href="'#/schedule-agenda/' + event.id" target="_blank" class="text-blue-600 hover:underline text-xs">
                     {{ index + 1 }}. {{ event.name || 'Unnamed Event' }}
                   </a>
                </div>
              </div>
              <i slot="reference" class="el-icon-view ml-2 cursor-pointer text-blue-500" title="View List"></i>
            </el-popover>
          </div>
        </div>

        <!-- Attachments to Hide Selection -->
        <div class="mb-4" v-if="matchingHideEvents.length > 0">
           <div class="block">
            Attachments Found
           </div>
           <div v-if="availableAttachmentsToHide.length === 0" class="text-sm text-gray-500 italic">
             No attachments found in the selected bookings.
           </div>
           
           <el-checkbox-group v-model="hideForm.selectedAttachments" v-else>
             <div v-for="attName in availableAttachmentsToHide" :key="attName" class="mb-1">
               <el-checkbox :label="attName">{{ attName }}</el-checkbox>
             </div>
           </el-checkbox-group>

           <div v-if="hideForm.selectedAttachments.length > 0" class="mt-2 text-xs text-yellow-600">
             <i class="el-icon-warning"></i> 
             These attachments will be hidden from <strong>ALL</strong> users except SysAdmins.
           </div>
        </div>

        <!-- Success Message -->
         <div v-if="hideSuccessMessage" class="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
            <div class="flex items-center">
              <i class="el-icon-success text-green-500 mr-2 text-xl"></i>
              <span class="text-green-700 font-bold">{{ hideSuccessMessage }}</span>
            </div>
         </div>


        <!-- Actions -->
        <div class="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-0 max-sm:[&_.el-button]:ml-0 max-sm:[&_.el-button]:w-full">
          <el-button @click="handleClose">Close</el-button>
          <el-button 
            type="danger" 
            @click="confirmHide" 
            :disabled="!isHideValid || uploading" 
            :loading="uploading">
            Hide Selected Attachments
          </el-button>
        </div>

      </div>

    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { mapState, mapActions } from 'vuex';
import { generateClient } from 'aws-amplify/api';
import { uploadData } from 'aws-amplify/storage';
import * as qlqueries from '../../graphql/queries';

const client = generateClient();

export default {
  name: 'BulkAttachments',
  data() {
    return {
      activeTab: 'add',
      loading: false,
      uploading: false,
      allMonthEvents: [], // All events fetches
      
      // ADD FORM STATE
      addForm: {
        client: null,
        accountId: 'ALL',
        tags: [],
        availableTags: [],
        cachedEvents: []
      },
      fileList: [],
      uploadedFileUrl: null,
      uploadedFileName: null,

      // HIDE FORM STATE
      hideForm: {
        client: null,
        accountId: 'ALL',
        tags: [],
        availableTags: [],
        cachedEvents: [],
        selectedAttachments: []
      },
      hideSuccessMessage: null
    };
  },
  computed: {
    ...mapState({
      userAccount: (state) => state.userAccount,
      listClients: (state) => state.userAccount.listClients,
      awsExports: (state) => state.awsExports,
    }),
    availableClients() {
      if (!this.listClients.items) return [];
      // Return list of clients sorted by name
      return [...this.listClients.items].sort((a, b) => a.name.localeCompare(b.name));
    },
    
    // ADD Computed
    matchingAddEvents() {
       if (this.addForm.tags.length === 0) return [];
       return this.filterEventsByTags(this.addForm.cachedEvents, this.addForm.tags);
    },
    isAddValid() {
      return this.addForm.client && this.addForm.tags.length > 0 && this.fileList.length > 0;
    },

    // HIDE Computed
    matchingHideEvents() {
       if (this.hideForm.tags.length === 0) return [];
       return this.filterEventsByTags(this.hideForm.cachedEvents, this.hideForm.tags);
    },
    availableAttachmentsToHide() {
      // Aggregate all attachment filenames from matching events
      const filenames = new Set();
      this.matchingHideEvents.forEach(event => {
        const atts = this.parseAttachments(event.attachments);
        atts.forEach(a => {
           // Skip if already hidden? No, allow user to re-hide or we can filter
           if (!a.hidden) filenames.add(a.fileName);
        });
      });
      return Array.from(filenames).sort();
    },
    isHideValid() {
      return this.hideForm.client && this.hideForm.tags.length > 0 && this.hideForm.selectedAttachments.length > 0;
    }
  },
  watch: {
    // When the component is mounted or visible, we might want to default the account
    userAccount: {
      handler(val) {
        if (val && val.client && !this.addForm.client) {
          // Default to current user's client for ADD form
          const defaultClient = this.availableClients.find(c => c.code === val.client.code);
          if (defaultClient) {
            this.addForm.client = defaultClient;
            this.addForm.accountId = 'ALL';
            this.updateAvailableTags('add');
            
            // Also default for HIDE form
             this.hideForm.client = defaultClient;
             this.hideForm.accountId = 'ALL';
             this.updateAvailableTags('hide');
          }
        }
      },
      immediate: true,
    }
  },
  mounted() {
    this.fetchAllEvents();
  },
  methods: {
    ...mapActions({
      updateEvent: 'schedule/updateEvent',
      signDownloadUrl: 'schedule/signDownloadUrl',
    }),
    
    getAvailableAccounts(client) {
      if (!client) return [];
      return [...client.accounts.items].sort((a, b) => a.name.localeCompare(b.name));
    },

    handleSelectionChange(formType) {
      if (formType === 'add') {
        this.addForm.tags = [];
        this.fileList = [];
        this.updateAvailableTags('add');
      } else {
        this.hideForm.tags = [];
        this.hideForm.selectedAttachments = [];
        this.hideSuccessMessage = null;
        this.updateAvailableTags('hide');
      }
    },
    onClientSelect(formType, clientId) {
      if (!clientId) return;
      const client = this.availableClients.find(c => c.id === clientId);
      
       if (formType === 'add') {
        this.addForm.client = client;
        this.addForm.accountId = 'ALL';
      } else {
        this.hideForm.client = client;
        this.hideForm.accountId = 'ALL';
      }
      this.handleSelectionChange(formType);
    },
    onAccountSelect(formType, accountId) {
      if (!accountId) return;
       if (formType === 'add') {
        this.addForm.accountId = accountId;
      } else {
        this.hideForm.accountId = accountId;
      }
      this.handleSelectionChange(formType);
    },

    handleTagChange() {
      // Just triggers computed property update
    },
    handleFileChange(file, fileList) {
      this.fileList = fileList.slice(-1); // Keep only last file
    },
    handleFileRemove(file, fileList) {
      this.fileList = fileList;
    },
    
    async fetchAllEvents() {
      this.loading = true;
      this.allMonthEvents = [];
      
      const start = moment().utc().subtract(6, 'days').toISOString();
      const end = moment().utc().add(1, 'month').toISOString();
      
      let allEvents = [];
      let nextToken = null;
      
      try {
        do {
          const variables = {
            type: 'OU',
            limit: 3000,
            sortDirection: 'ASC',
            startTime: { between: [start, end] },
            nextToken,
          };
          
          const result = await client.graphql({
            query: qlqueries.eventsByStartTimeTags,
            variables,
          });
          
          if (result.data && result.data.eventsByStartTime) {
            allEvents = allEvents.concat(result.data.eventsByStartTime.items);
            nextToken = result.data.eventsByStartTime.nextToken;
          } else {
            nextToken = null;
          }
        } while (nextToken);
        
        this.allMonthEvents = allEvents;
        
        // Update tags for both forms if client selected
        if (this.addForm.client) this.updateAvailableTags('add');
        if (this.hideForm.client) this.updateAvailableTags('hide');

      } catch (error) {
        console.error("Error fetching tags:", error);
        this.$message.error("Failed to load events.");
      } finally {
        this.loading = false;
      }
    },

    updateAvailableTags(formType) {
        const form = formType === 'add' ? this.addForm : this.hideForm;

        if (!form.client || !this.allMonthEvents.length) {
          form.availableTags = [];
          form.cachedEvents = [];
          return;
        }

        const clientCode = form.client.code;
        
        // Filter by selected client
        let events = this.allMonthEvents.filter(event => 
          event.customer && event.customer.code === clientCode
        );

        // Filter by account
        if (form.accountId !== 'ALL') {
             // Find account code from available accounts
             // We can't use form.account object directly if we just stored ID
             const accounts = form.client.accounts.items;
             const account = accounts.find(a => a.id === form.accountId);
             if (account) {
                 const accountCode = account.code;
                 events = events.filter(event => event.account && event.account.code === accountCode);
             }
        }
        
        form.cachedEvents = events;
        
        // Extract tags
        const tagsSet = new Set();
        form.cachedEvents.forEach(event => {
          if (event.tags && Array.isArray(event.tags)) {
            event.tags.forEach(tag => tagsSet.add(tag));
          }
        });
        form.availableTags = Array.from(tagsSet).sort();
    },
    
    filterEventsByTags(events, selectedTags) {
      if (selectedTags.length === 0) return events;
      return events.filter(event => {
        if (!event.tags) return false;
        // Check if event has ALL selected tags
        return selectedTags.every(paramsTag => event.tags.includes(paramsTag));
      });
    },
    
    // Helper to parse attachments
    parseAttachments(attachmentsStr, event) {
        if (!attachmentsStr) return [];
        try {
            if (attachmentsStr.trim().startsWith('[')) {
                return JSON.parse(attachmentsStr);
            } else if (attachmentsStr.trim().startsWith('{')) {
                const json = JSON.parse(attachmentsStr);
                const list = [];
                 if (json.tagAttachments) {
                     list.push(...json.tagAttachments.map(a => ({ ...a, type: 'TAG' })));
                 }
                 if (json.regularAttachments) {
                     list.push(...json.regularAttachments.map(a => ({ ...a, type: 'REGULAR' })));
                 }
                 return list;
            } else {
                 // old format
                 const list = attachmentsStr.split(',');
                 return list.filter(f => f && f.trim() !== '').map(f => ({
                       fileName: f.trim(),
                       key: null, // Legacy often relies on constructing key elsewhere
                       type: 'REGULAR'
                 }));
            }
        } catch (e) {
            console.error('Parse error', e);
            return [];
        }
    },

    confirmUpload() {
      const targetEvents = this.matchingAddEvents;
      if (targetEvents.length === 0) {
        this.$message.warning("No events match the selected tags.");
        return;
      }
      
      this.$confirm(
        `This will upload the file and attach it to ${targetEvents.length} bookings. Continue?`,
        'Confirm Bulk Attachment',
        {
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          type: 'warning'
        }
      ).then(() => {
        this.startUploadProcess(targetEvents);
      }).catch(() => {});
    },
    
    async startUploadProcess(targetEvents) {
      this.uploading = true;
      try {
        const file = this.fileList[0].raw;
        const clientCode = this.addForm.client.code;

        // Generate S3 Key (Client Level)
        const name = file.name.substring(0, file.name.lastIndexOf('.'));
        const extension = file.name.substring(file.name.lastIndexOf('.'));
        const timestamp = moment.utc().format('YYYYMMDD-HHmmss');
        const keyFileName = `${timestamp}-${name}${extension}`;
        const s3Key = `${clientCode}/${keyFileName}`;

        // Upload to S3 (Once)
        await uploadData({
          key: s3Key,
          data: file,
          options: {
            metadata: {
              account: this.addForm.accountId === 'ALL' ? 'ALL' : this.addForm.accountId, // Ideally code
              client: clientCode,
              tags: this.addForm.tags.join(','),
            }
          }
        }).result;

        let totalSuccess = 0;
        let totalFail = 0;

        for (const event of targetEvents) {
            try {
              let attachmentsList = this.parseAttachments(event.attachments) || [];
              
              // Add New Attachment (Avoid Duplicates)
              const exists = attachmentsList.find(a => a.key === s3Key);
              if (!exists) {
                attachmentsList.push({
                   fileName: keyFileName,
                   key: s3Key,
                   type: 'TAG'
                });
                
                const newAttachmentsStr = JSON.stringify(attachmentsList);

                await this.updateEvent({
                  id: event.id,
                  attachments: newAttachmentsStr,
                  expectedVersion: event.version 
                });
                totalSuccess++;
              } else {
                 // Migrate to JSON if needed
                 if (!event.attachments || !event.attachments.trim().startsWith('[')) {
                     const newAttachmentsStr = JSON.stringify(attachmentsList);
                     await this.updateEvent({
                        id: event.id,
                        attachments: newAttachmentsStr,
                        expectedVersion: event.version 
                     });
                     totalSuccess++;
                 }
              }

            } catch (err) {
              console.error(`Failed to update event ${event.id}`, err);
              totalFail++;
            }
        }
        
        // Generate download URL for success message
        if (s3Key) {
          const signedUrl = await this.signDownloadUrl(`public/${s3Key}`);
          this.uploadedFileUrl = signedUrl;
          this.uploadedFileName = file.name;
        }

        this.$message.success(`Attachment added to ${totalSuccess} bookings.`);
        if (totalFail > 0) {
          this.$message.warning(`${totalFail} bookings failed to update.`);
        }

      } catch (error) {
        console.error("Upload failed", error);
        this.$message.error("File upload failed.");
      } finally {
        this.uploading = false;
      }
    },


    confirmHide() {
      const targetEvents = this.matchingHideEvents;
       if (targetEvents.length === 0) {
        this.$message.warning("No events match specific tags.");
        return;
      }
      
      const filesToHide = this.hideForm.selectedAttachments.join(', ');

      this.$confirm(
        `This will hide the following attachments: [${filesToHide}] from ${targetEvents.length} potential bookings. Continue?`,
        'Confirm Hide Attachments',
        {
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          type: 'warning'
        }
      ).then(() => {
        this.startHideProcess(targetEvents);
      }).catch(() => {});
    },

    async startHideProcess(targetEvents) {
        this.uploading = true;
        let totalSuccess = 0;
        let totalFail = 0;
        
        try {
           for (const event of targetEvents) {
              try {
                  let attachmentsList = this.parseAttachments(event.attachments) || [];
                  let changed = false;

                  attachmentsList.forEach(att => {
                      if (this.hideForm.selectedAttachments.includes(att.fileName)) {
                          if (!att.hidden) {
                              att.hidden = true;
                              changed = true;
                          }
                      }
                  });

                  if (changed) {
                      const newAttachmentsStr = JSON.stringify(attachmentsList);
                      await this.updateEvent({
                        id: event.id,
                        attachments: newAttachmentsStr,
                        expectedVersion: event.version 
                      });
                      totalSuccess++;
                  }

              } catch (err) {
                  console.error(`Failed to update event ${event.id}`, err);
                  totalFail++;
              }
           }
           
           this.hideSuccessMessage = `Successfully hidden attachments in ${totalSuccess} bookings.`;
           this.$message.success('Bulk hide operation completed.');

        } catch (error) {
          console.error("Bulk hide failed", error);
          this.$message.error("Operation failed.");
        } finally {
          this.uploading = false;
        }
    },

    handleClose() {
      if (this.uploadedFileUrl) {
        this.$emit('uploaded');
      } else {
        this.$emit('close');
      }
    },
  }
};
</script>

